import { useEffect, useState } from 'react'

import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { axiosInstance } from '../helpers'
import { InternalInvoice, PaymentForm } from '../models'

export interface InvoiceFilters {
    invoiceNumber?: string
    status?: string[]
    draftSubStatus?: string
    startDate?: string
    endDate?: string
    company?: number
    balance?: number
    balanceOperator?: 'eq' | 'gt' | 'lt' | 'gte' | 'lte'
    organization?: number
    billingGroup?: number
}

interface PaginationParams {
    limit: number
    offset: number
    sort?: string
    property_id?: number
    invoice_number?: string
    status?: string[]
    start_date?: string
    end_date?: string
    company?: number
    balance?: number
    balance_operator?: string
    all_properties?: boolean
    isAdmin?: boolean
}

interface PaginatedResponse {
    results: InternalInvoice[]
    count: number
}

interface StatusCount {
    _status: string
    count: number
}

export interface InvoiceSummary {
    total_invoices: number
    total_amount: number
    total_paid: number
    total_overdue: number
    avg_days_to_pay: number
    status_counts: StatusCount[]
}

export interface PaymentTrends {
    [month: string]: {
        total_paid: number
        total_invoiced: number
    }
}

export interface BillableOrgs {
    id: number
    name: string
}

export interface UpdateInvoiceDatesRequest {
    invoiceId: string
    issue_date: string
    due_date: string
}

export interface AddLineItemRequest {
    invoiceId: string
    description: string
    tax_type?: string
    tax_rate?: string
    subtotal?: string
    quantity?: string
    unit_price?: string
}

export interface EditLineItemRequest {
    invoiceId: string
    lineItemId: string
    description?: string
    quantity?: string
    unit_price?: string
    subtotal?: string
}

export interface RemoveLineItemRequest {
    invoiceId: string
    lineItemId: string
}

// Type for valid sort fields
export type ValidSortField =
    | 'created_at'
    | 'issue_date'
    | 'total_amount'
    | 'status'
    | 'company'
    | 'invoice_number'
    | '-created_at'
    | '-issue_date'
    | '-total_amount'
    | '-status'
    | '-company'
    | '-invoice_number'

export const useBilling = ({
    limit,
    offset,
    sort,
    property_id: workspaceId,
    isAdmin = false,
}: PaginationParams) => {
    const queryClient = useQueryClient()
    const [filters, setFilters] = useState<InvoiceFilters>({
        startDate: '2025-01-01',
    })
    const [appliedFilters, setAppliedFilters] = useState<InvoiceFilters>({
        startDate: '2025-01-01',
    })
    const [isApplyingFilters, setIsApplyingFilters] = useState(false)

    const queryKey = [
        'billing',
        limit,
        offset,
        sort,
        workspaceId,
        isAdmin,
        appliedFilters.invoiceNumber,
        appliedFilters.status,
        appliedFilters.draftSubStatus,
        appliedFilters.startDate,
        appliedFilters.endDate,
        appliedFilters.company,
        appliedFilters.balance,
        appliedFilters.balanceOperator,
        appliedFilters.organization,
        appliedFilters.billingGroup,
    ]
    const summaryQueryKey = [
        'billing-summary',
        workspaceId,
        isAdmin,
        appliedFilters.invoiceNumber,
        appliedFilters.status,
        appliedFilters.startDate,
        appliedFilters.endDate,
        appliedFilters.company,
        appliedFilters.balance,
        appliedFilters.balanceOperator,
        appliedFilters.organization,
        appliedFilters.billingGroup,
    ]
    const paymentTrendsKey = [
        'billing-payment-trends',
        workspaceId,
        isAdmin,
        appliedFilters.invoiceNumber,
        appliedFilters.status,
        appliedFilters.startDate,
        appliedFilters.endDate,
        appliedFilters.company,
        appliedFilters.balance,
        appliedFilters.balanceOperator,
        appliedFilters.organization,
        appliedFilters.billingGroup,
    ]

    const billableOrgsKey = ['billable-organizations', workspaceId, isAdmin]

    useEffect(() => {
        queryClient.invalidateQueries({ queryKey })
    }, [sort, isAdmin])

    const { data, isLoading, error, refetch } = useQuery<
        AxiosResponse<PaginatedResponse>,
        Error
    >({
        queryKey,
        queryFn: () =>
            axiosInstance.get('/billing/', {
                params: {
                    limit,
                    offset,
                    ...(sort && { sort }),
                    ...(appliedFilters.invoiceNumber && {
                        invoice_number: appliedFilters.invoiceNumber,
                    }),
                    ...(appliedFilters.status && {
                        status: appliedFilters.status.join(','),
                    }),
                    ...(appliedFilters.draftSubStatus && {
                        draft_sub_status: appliedFilters.draftSubStatus,
                    }),
                    ...(appliedFilters.startDate && {
                        start_date: appliedFilters.startDate,
                    }),
                    ...(appliedFilters.endDate && {
                        end_date: appliedFilters.endDate,
                    }),
                    ...(appliedFilters.company && {
                        company: appliedFilters.company,
                    }),
                    ...(appliedFilters.balance && {
                        balance: appliedFilters.balance,
                    }),
                    ...(appliedFilters.balanceOperator && {
                        balance_operator: appliedFilters.balanceOperator,
                    }),
                    ...(appliedFilters.organization && {
                        organization: appliedFilters.organization,
                    }),
                    ...(appliedFilters.billingGroup && {
                        billing_group: appliedFilters.billingGroup,
                    }),
                    ...(isAdmin
                        ? { all_properties: true }
                        : { property_id: workspaceId }),
                },
            }),
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    const {
        data: summaryData,
        isLoading: isSummaryLoading,
        refetch: refetchSummary,
    } = useQuery<AxiosResponse<InvoiceSummary>, Error>({
        queryKey: summaryQueryKey,
        queryFn: () =>
            axiosInstance.get('/billing/summary/', {
                params: {
                    limit,
                    offset,
                    ...(sort && { sort }),
                    ...(appliedFilters.invoiceNumber && {
                        invoice_number: appliedFilters.invoiceNumber,
                    }),
                    ...(appliedFilters.status && {
                        status: appliedFilters.status.join(','),
                    }),
                    ...(appliedFilters.draftSubStatus && {
                        draft_sub_status: appliedFilters.draftSubStatus,
                    }),
                    ...(appliedFilters.startDate && {
                        start_date: appliedFilters.startDate,
                    }),
                    ...(appliedFilters.endDate && {
                        end_date: appliedFilters.endDate,
                    }),
                    ...(appliedFilters.company && {
                        company: appliedFilters.company,
                    }),
                    ...(appliedFilters.balance && {
                        balance: appliedFilters.balance,
                    }),
                    ...(appliedFilters.balanceOperator && {
                        balance_operator: appliedFilters.balanceOperator,
                    }),
                    ...(appliedFilters.organization && {
                        organization: appliedFilters.organization,
                    }),
                    ...(appliedFilters.billingGroup && {
                        billing_group: appliedFilters.billingGroup,
                    }),
                    ...(isAdmin
                        ? { all_properties: true }
                        : { property_id: workspaceId }),
                },
            }),
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    const {
        data: billableOrgsData,
        isLoading: isBillableOrgsLoading,
        refetch: refetchBillableOrgs,
    } = useQuery<AxiosResponse<BillableOrgs[]>, Error>({
        queryKey: billableOrgsKey,
        queryFn: () =>
            axiosInstance.get('/billing/billable-organization/', {
                params: {
                    ...(isAdmin
                        ? { all_properties: true }
                        : { property_id: workspaceId }),
                },
            }),
    })

    const {
        data: paymentTrendsData,
        isLoading: isPaymentTrendsLoading,
        refetch: refetchPaymentTrends,
    } = useQuery<AxiosResponse<PaymentTrends>, Error>({
        queryKey: paymentTrendsKey,
        queryFn: () =>
            axiosInstance.get('/billing/payment-trends/', {
                params: {
                    limit,
                    offset,
                    ...(sort && { sort }),
                    ...(appliedFilters.invoiceNumber && {
                        invoice_number: appliedFilters.invoiceNumber,
                    }),
                    ...(appliedFilters.status && {
                        status: appliedFilters.status.join(','),
                    }),
                    ...(appliedFilters.draftSubStatus && {
                        draft_sub_status: appliedFilters.draftSubStatus,
                    }),
                    ...(appliedFilters.startDate && {
                        start_date: appliedFilters.startDate,
                    }),
                    ...(appliedFilters.endDate && {
                        end_date: appliedFilters.endDate,
                    }),
                    ...(appliedFilters.company && {
                        company: appliedFilters.company,
                    }),
                    ...(appliedFilters.balance && {
                        balance: appliedFilters.balance,
                    }),
                    ...(appliedFilters.balanceOperator && {
                        balance_operator: appliedFilters.balanceOperator,
                    }),
                    ...(appliedFilters.organization && {
                        organization: appliedFilters.organization,
                    }),
                    ...(appliedFilters.billingGroup && {
                        billing_group: appliedFilters.billingGroup,
                    }),
                    ...(isAdmin
                        ? { all_properties: true }
                        : { property_id: workspaceId }),
                },
            }),
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    const {
        mutateAsync: bulkSendInvoices,
        isLoading: isBulkSendLoading,
        error: bulkSendError,
    } = useMutation({
        mutationFn: ({
            invoiceIds,
            includeAll,
        }: {
            invoiceIds: number[]
            includeAll: boolean
        }) => {
            const requestBody: { invoice_ids?: number[] } = {}
            if (!includeAll) {
                requestBody.invoice_ids = invoiceIds
            }

            return axiosInstance.post('/billing/send-invoices/', requestBody, {
                params: {
                    limit,
                    offset,
                    ...(sort && { sort }),
                },
            })
        },
        onSuccess: async () => {
            // Invalidate all billing-related queries to ensure data is fresh
            await queryClient.invalidateQueries({ queryKey: ['billing'] })
            await queryClient.invalidateQueries({
                queryKey: ['billing-summary'],
            })
            await queryClient.invalidateQueries({
                queryKey: ['billing-payment-trends'],
            })

            // Also invalidate the specific query keys to be extra sure
            await queryClient.invalidateQueries({ queryKey })
            await queryClient.invalidateQueries({ queryKey: summaryQueryKey })
            await queryClient.invalidateQueries({ queryKey: paymentTrendsKey })
        },
    })

    const {
        mutateAsync: recordPayment,
        isLoading: isRecordPaymentLoading,
        error: recordPaymentError,
    } = useMutation({
        mutationFn: (request: PaymentForm) => {
            return axiosInstance.post(
                `/billing/${request.invoiceId}/record-payment/`,
                {
                    payment_amount: request.payment_amount,
                    payment_date: request.payment_date,
                    payment_method: request.payment_method,
                    payment_reference: request.payment_reference,
                },
            )
        },
        onSuccess: async () => {
            // Invalidate all billing-related queries to ensure data is fresh
            await queryClient.invalidateQueries({ queryKey: ['billing'] })
            await queryClient.invalidateQueries({
                queryKey: ['billing-summary'],
            })
            await queryClient.invalidateQueries({
                queryKey: ['billing-payment-trends'],
            })

            // Also invalidate the specific query keys to be extra sure
            await queryClient.invalidateQueries({ queryKey })
            await queryClient.invalidateQueries({ queryKey: summaryQueryKey })
            await queryClient.invalidateQueries({ queryKey: paymentTrendsKey })
        },
        onError: (error: any) => {
            // Check if this is a QuickBooks authorization error
            if (
                error.response?.data?.requires_authorization &&
                error.response?.data?.authorize_url
            ) {
                toast.error(
                    'QuickBooks authorization required. Redirecting to authorization page...',
                )
                // Redirect to the authorization URL
                window.location.href = error.response.data.authorize_url
                return
            }
        },
    })

    const {
        mutateAsync: voidInvoice,
        isLoading: isVoidInvoiceLoading,
        error: voidInvoiceError,
    } = useMutation({
        mutationFn: ({
            invoiceId,
            reason,
        }: {
            invoiceId: string
            reason: string
        }) => {
            return axiosInstance.post(`/billing/${invoiceId}/void/`, {
                reason,
            })
        },
        onSuccess: async () => {
            // Invalidate all billing-related queries to ensure data is fresh
            await queryClient.invalidateQueries({ queryKey: ['billing'] })
            await queryClient.invalidateQueries({
                queryKey: ['billing-summary'],
            })
            await queryClient.invalidateQueries({
                queryKey: ['billing-payment-trends'],
            })

            // Also invalidate the specific query keys to be extra sure
            await queryClient.invalidateQueries({ queryKey })
            await queryClient.invalidateQueries({ queryKey: summaryQueryKey })
            await queryClient.invalidateQueries({ queryKey: paymentTrendsKey })
        },
        onError: (error: any) => {
            // Check if this is a QuickBooks authorization error
            if (
                error.response?.data?.requires_authorization &&
                error.response?.data?.authorize_url
            ) {
                toast.error(
                    'QuickBooks authorization required. Redirecting to authorization page...',
                )
                // Redirect to the authorization URL
                window.location.href = error.response.data.authorize_url
                return
            }
        },
    })

    const {
        mutateAsync: markReviewed,
        isLoading: isMarkReviewedLoading,
        error: markReviewedError,
    } = useMutation({
        mutationFn: ({ invoiceIds }: { invoiceIds: number[] }) => {
            return axiosInstance.post(`/billing/mark-reviewed/`, {
                invoice_ids: invoiceIds,
            })
        },
        onSuccess: async () => {
            // Invalidate all billing-related queries to ensure data is fresh
            await queryClient.invalidateQueries({ queryKey: ['billing'] })
            await queryClient.invalidateQueries({
                queryKey: ['billing-summary'],
            })
            await queryClient.invalidateQueries({
                queryKey: ['billing-payment-trends'],
            })

            // Also invalidate the specific query keys to be extra sure
            await queryClient.invalidateQueries({ queryKey })
            await queryClient.invalidateQueries({ queryKey: summaryQueryKey })
            await queryClient.invalidateQueries({ queryKey: paymentTrendsKey })
        },
        onError: (error: any) => {
            // Check if this is a QuickBooks authorization error
            if (
                error.response?.data?.requires_authorization &&
                error.response?.data?.authorize_url
            ) {
                toast.error(
                    'QuickBooks authorization required. Redirecting to authorization page...',
                )
                // Redirect to the authorization URL
                window.location.href = error.response.data.authorize_url
                return
            }
        },
    })

    const {
        mutateAsync: voidPayment,
        isLoading: isVoidPaymentLoading,
        error: voidPaymentError,
    } = useMutation({
        mutationFn: ({
            invoiceId,
            paymentId,
            reason,
        }: {
            invoiceId: string
            paymentId: string
            reason: string
        }) => {
            return axiosInstance.post(`/billing/${paymentId}/void-payment/`, {
                reason,
            })
        },
        onSuccess: async () => {
            // Invalidate all billing-related queries to ensure data is fresh
            await queryClient.invalidateQueries({ queryKey: ['billing'] })
            await queryClient.invalidateQueries({
                queryKey: ['billing-summary'],
            })
            await queryClient.invalidateQueries({
                queryKey: ['billing-payment-trends'],
            })

            // Also invalidate the specific query keys to be extra sure
            await queryClient.invalidateQueries({ queryKey })
            await queryClient.invalidateQueries({ queryKey: summaryQueryKey })
            await queryClient.invalidateQueries({ queryKey: paymentTrendsKey })
        },
        onError: (error: any) => {
            // Check if this is a QuickBooks authorization error
            if (
                error.response?.data?.requires_authorization &&
                error.response?.data?.authorize_url
            ) {
                toast.error(
                    'QuickBooks authorization required. Redirecting to authorization page...',
                )
                // Redirect to the authorization URL
                window.location.href = error.response.data.authorize_url
                return
            }
        },
    })

    const {
        mutateAsync: updateInvoiceDates,
        isLoading: isUpdateInvoiceDatesLoading,
        error: updateInvoiceDatesError,
    } = useMutation({
        mutationFn: ({
            invoiceId,
            issue_date,
            due_date,
        }: UpdateInvoiceDatesRequest) => {
            return axiosInstance.patch(`/billing/${invoiceId}/`, {
                issue_date,
                due_date,
            })
        },
        onSuccess: async (response, { invoiceId }) => {
            // Update the specific invoice in the cached data using response data
            queryClient.setQueryData(queryKey, (oldData: any) => {
                if (!oldData?.data?.results) return oldData

                return {
                    ...oldData,
                    data: {
                        ...oldData.data,
                        results: oldData.data.results.map(
                            (invoice: InternalInvoice) =>
                                invoice.id === invoiceId
                                    ? { ...invoice, ...response.data }
                                    : invoice,
                        ),
                    },
                }
            })

            // Also update summary and payment trends queries
            await queryClient.invalidateQueries({
                queryKey: ['billing-summary'],
            })
            await queryClient.invalidateQueries({
                queryKey: ['billing-payment-trends'],
            })

            // Invalidate other billing queries to ensure consistency
            await queryClient.invalidateQueries({ queryKey: ['billing'] })

            toast.success('Invoice dates updated successfully')
        },
        onError: (error: any) => {
            toast.error('Failed to update invoice dates')
            console.error('Error updating invoice dates:', error)
        },
    })

    const {
        mutateAsync: addLineItem,
        isLoading: isAddLineItemLoading,
        error: addLineItemError,
    } = useMutation({
        mutationFn: ({
            invoiceId,
            description,
            tax_type,
            tax_rate,
            subtotal,
            quantity,
            unit_price,
        }: AddLineItemRequest) => {
            const requestData: any = {
                description,
            }

            if (tax_type) requestData.tax_type = tax_type
            if (tax_rate) requestData.tax_rate = tax_rate
            if (subtotal) requestData.subtotal = subtotal
            if (quantity) requestData.quantity = quantity
            if (unit_price) requestData.unit_price = unit_price

            return axiosInstance.post(
                `/billing/${invoiceId}/add-line-item/`,
                requestData,
            )
        },
        onSuccess: async (response, { invoiceId }) => {
            // Update the specific invoice in the cached data using response data
            queryClient.setQueryData(queryKey, (oldData: any) => {
                if (!oldData?.data?.results) return oldData

                return {
                    ...oldData,
                    data: {
                        ...oldData.data,
                        results: oldData.data.results.map(
                            (invoice: InternalInvoice) =>
                                invoice.id === invoiceId
                                    ? { ...invoice, ...response.data }
                                    : invoice,
                        ),
                    },
                }
            })

            // Also update summary and payment trends queries
            await queryClient.invalidateQueries({
                queryKey: ['billing-summary'],
            })
            await queryClient.invalidateQueries({
                queryKey: ['billing-payment-trends'],
            })

            // Invalidate other billing queries to ensure consistency
            await queryClient.invalidateQueries({ queryKey: ['billing'] })

            toast.success('Line item added successfully')
        },
        onError: (error: any) => {
            toast.error('Failed to add line item')
            console.error('Error adding line item:', error)
        },
    })

    const {
        mutateAsync: editLineItem,
        isLoading: isEditLineItemLoading,
        error: editLineItemError,
    } = useMutation({
        mutationFn: ({
            invoiceId,
            lineItemId,
            description,
            quantity,
            unit_price,
            subtotal,
        }: EditLineItemRequest) => {
            const requestData: any = {}

            if (description !== undefined) requestData.description = description
            if (quantity !== undefined) requestData.quantity = quantity
            if (unit_price !== undefined) requestData.unit_price = unit_price
            if (subtotal !== undefined) requestData.subtotal = subtotal

            return axiosInstance.put(
                `/billing/${invoiceId}/edit-line-item/${lineItemId}/`,
                requestData,
            )
        },
        onSuccess: async (response, { invoiceId }) => {
            // Update the specific invoice in the cached data using response data
            queryClient.setQueryData(queryKey, (oldData: any) => {
                if (!oldData?.data?.results) return oldData

                return {
                    ...oldData,
                    data: {
                        ...oldData.data,
                        results: oldData.data.results.map(
                            (invoice: InternalInvoice) =>
                                invoice.id === invoiceId
                                    ? { ...invoice, ...response.data }
                                    : invoice,
                        ),
                    },
                }
            })

            // Also update summary and payment trends queries
            await queryClient.invalidateQueries({
                queryKey: ['billing-summary'],
            })
            await queryClient.invalidateQueries({
                queryKey: ['billing-payment-trends'],
            })

            // Invalidate other billing queries to ensure consistency
            await queryClient.invalidateQueries({ queryKey: ['billing'] })

            toast.success('Line item updated successfully')
        },
        onError: (error: any) => {
            toast.error('Failed to update line item')
            console.error('Error updating line item:', error)
        },
    })

    const {
        mutateAsync: removeLineItem,
        isLoading: isRemoveLineItemLoading,
        error: removeLineItemError,
    } = useMutation({
        mutationFn: ({ invoiceId, lineItemId }: RemoveLineItemRequest) => {
            return axiosInstance.delete(
                `/billing/${invoiceId}/remove-line-item/${lineItemId}/`,
            )
        },
        onSuccess: async (response, { invoiceId }) => {
            // Update the specific invoice in the cached data using response data
            queryClient.setQueryData(queryKey, (oldData: any) => {
                if (!oldData?.data?.results) return oldData

                return {
                    ...oldData,
                    data: {
                        ...oldData.data,
                        results: oldData.data.results.map(
                            (invoice: InternalInvoice) =>
                                invoice.id === invoiceId
                                    ? { ...invoice, ...response.data }
                                    : invoice,
                        ),
                    },
                }
            })

            // Also update summary and payment trends queries
            await queryClient.invalidateQueries({
                queryKey: ['billing-summary'],
            })
            await queryClient.invalidateQueries({
                queryKey: ['billing-payment-trends'],
            })

            // Invalidate other billing queries to ensure consistency
            await queryClient.invalidateQueries({ queryKey: ['billing'] })

            toast.success('Line item removed successfully')
        },
        onError: (error: any) => {
            toast.error('Failed to remove line item')
            console.error('Error removing line item:', error)
        },
    })

    const handleFiltersChange = (newFilters: InvoiceFilters) => {
        setFilters(newFilters)
    }

    const handleApplyFilters = async (newFilters?: InvoiceFilters) => {
        setIsApplyingFilters(true)
        setAppliedFilters(newFilters || filters)
        await queryClient.invalidateQueries({ queryKey: queryKey })
        await queryClient.invalidateQueries({ queryKey: summaryQueryKey })
        await queryClient.invalidateQueries({ queryKey: paymentTrendsKey })
        setIsApplyingFilters(false)
    }

    const handleResetFilters = () => {
        setFilters({})
        setAppliedFilters({})
    }

    const exportCsv = async () => {
        const params: Record<string, string> = {}

        if (appliedFilters.invoiceNumber) {
            params.invoice_number = appliedFilters.invoiceNumber
        }
        if (appliedFilters.status && appliedFilters.status.length > 0) {
            params.status = appliedFilters.status.join(',')
        }
        if (appliedFilters.draftSubStatus) {
            params.draft_sub_status = appliedFilters.draftSubStatus
        }
        if (appliedFilters.startDate) {
            params.start_date = appliedFilters.startDate
        }
        if (appliedFilters.endDate) {
            params.end_date = appliedFilters.endDate
        }
        if (appliedFilters.company) {
            params.company = String(appliedFilters.company)
        }
        if (appliedFilters.balance) {
            params.balance = String(appliedFilters.balance)
        }
        if (appliedFilters.balanceOperator) {
            params.balance_operator = appliedFilters.balanceOperator
        }
        if (appliedFilters.organization) {
            params.organization = String(appliedFilters.organization)
        }
        if (appliedFilters.billingGroup) {
            params.billing_group = String(appliedFilters.billingGroup)
        }
        if (isAdmin) {
            params.all_properties = 'true'
        } else if (workspaceId) {
            params.property_id = String(workspaceId)
        }

        const response = await axiosInstance.get('/billing/export-csv/', {
            params,
            responseType: 'blob',
        })

        // Create a download link and trigger the download
        const blob = new Blob([response.data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url

        // Generate filename with current date
        const date = new Date().toISOString().split('T')[0]
        link.download = `invoices-${date}.csv`

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
    }

    return {
        data,
        isLoading,
        error,
        refetch,
        bulkSendInvoices,
        isBulkSendLoading,
        bulkSendError,
        recordPayment,
        isRecordPaymentLoading,
        recordPaymentError,
        voidInvoice,
        markReviewed,
        voidPayment,
        updateInvoiceDates,
        isUpdateInvoiceDatesLoading,
        updateInvoiceDatesError,
        addLineItem,
        isAddLineItemLoading,
        addLineItemError,
        editLineItem,
        isEditLineItemLoading,
        editLineItemError,
        removeLineItem,
        isRemoveLineItemLoading,
        removeLineItemError,
        summaryData: summaryData?.data,
        isSummaryLoading,
        refetchSummary,
        billableOrgs: billableOrgsData?.data,
        isBillableOrgsLoading,
        refetchBillableOrgs: () => refetchBillableOrgs(),
        paymentTrends: paymentTrendsData?.data,
        isPaymentTrendsLoading,
        refetchPaymentTrends: () => refetchPaymentTrends(),

        filters,
        appliedFilters,
        isApplyingFilters,
        handleFiltersChange,
        handleApplyFilters,
        handleResetFilters,
        exportCsv,
    }
}

export const getDefaultSummaryData: () => InvoiceSummary = () => ({
    total_invoices: 0,
    total_amount: 0,
    total_paid: 0,
    total_overdue: 0,
    avg_days_to_pay: 0,
    status_counts: [
        { _status: 'DRAFT', count: 0 },
        { _status: 'OVERDUE', count: 0 },
        { _status: 'PAID', count: 0 },
        { _status: 'SENT', count: 0 },
    ],
})
